import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtCcy, fmtUSDStrict } from "@/lib/format";
import { HalalBadge } from "@/components/common";
import { ContractNote } from "@/components/ContractNote";
import { StorageNote } from "@/components/StorageNote";
import { OrderPreview } from "@/components/OrderPreview";
import { StopPanel } from "@/components/order/StopPanel";
import { CostSummary, QuantityField } from "@/components/order/TicketFields";
import { toastError } from "@/lib/toastError";

export const OrderTicket = ({ open, onOpenChange, symbol, source = "global", onDone,
  initialSide = "buy", initialType = "market", maxQty }) => {
  const [quote, setQuote] = useState(null);
  const [side, setSide] = useState(initialSide);
  const [qty, setQty] = useState(1);
  const [orderType, setOrderType] = useState(initialType);
  const [limitPrice, setLimitPrice] = useState("");
  const [stopPrice, setStopPrice] = useState("");
  const [trailPct, setTrailPct] = useState(null);   // null = fixed stop
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    if (!open || !symbol) return;
    setSide(initialSide); setOrderType(initialType);
    setLimitPrice(""); setStopPrice(""); setTrailPct(null); setPreview(null);
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/market/quote/${symbol}`);
        if (!active) return;
        setQuote((prev) => {
          if (!prev) setQty(maxQty || (data.contract ? data.contract.min_order : 1));
          if (initialType === "stop") setStopPrice((s) => s || (data.price * 0.9).toFixed(2));
          return data;
        });
      } catch (e) { console.error("Failed to load quote:", e); }
    };
    load();
    const iv = setInterval(load, 4000);
    return () => { active = false; clearInterval(iv); };
  }, [open, symbol, initialSide, initialType, maxQty]);

  if (!quote) return null;
  const contract = quote.contract;
  const minQty = contract ? contract.min_order : 0.01;
  const belowMin = qty < minQty;
  const isStop = orderType === "stop";
  const trailing = isStop && trailPct != null;
  const price = orderType === "limit" && limitPrice ? Number(limitPrice)
    : trailing ? quote.price * (1 - trailPct / 100)
    : isStop && stopPrice ? Number(stopPrice) : quote.price;
  const fxToUsd = { USD: 1, QAR: 0.2747, SAR: 0.2666, AED: 0.2723, KWD: 3.25, BHD: 2.6596, OMR: 2.6008 };
  const estLocal = price * qty;
  const estUsd = estLocal * (fxToUsd[quote.currency] || 1);
  const fee = estUsd * 0.001;

  const submit = async (approvalToken) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", { symbol, side, qty: Number(qty),
        order_type: trailing ? "trailing_stop" : orderType,
        limit_price: orderType === "limit" ? Number(limitPrice) : null,
        stop_price: isStop && !trailing ? Number(stopPrice) : null,
        trail_percent: trailing ? trailPct : null, source,
        approval_token: approvalToken || null });
      if (data.working) {
        toast.success(trailing ? `Trailing stop armed — ${symbol}`
          : isStop ? `Stop loss armed — ${symbol}` : `Limit ${side} working — ${symbol}`, {
          description: `${data.message}${approvalToken ? " · approved with Face ID" : ""}`,
          duration: 5000,
        });
      } else {
        toast.success(`${side === "buy" ? "Bought" : "Sold"} ${qty} ${symbol}`, {
          description: `Filled @ ${fmtCcy(price, quote.currency)}${approvalToken ? " · approved with Face ID" : ""}`,
          duration: 3000,
        });
      }
      onOpenChange(false);
      setPreview(null);
      onDone && onDone();
    } catch (e) {
      toast.error("Order failed", { description: e?.response?.data?.detail || "Please try again" });
    } finally { setSubmitting(false); }
  };

  const review = async () => {
    setReviewing(true);
    try {
      const { data } = await api.post("/orders/preview", { symbol, side, qty: Number(qty),
        order_type: trailing ? "trailing_stop" : orderType,
        limit_price: orderType === "limit" ? Number(limitPrice) : null,
        stop_price: isStop && !trailing ? Number(stopPrice) : null,
        trail_percent: trailing ? trailPct : null, source });
      setPreview(data);
    } catch (e) { toastError("Couldn't prepare your order", e); }
    finally { setReviewing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="order-ticket">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{symbol}</span>
            {quote.halal && <HalalBadge />}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{quote.name}</p>
        </DialogHeader>

        {preview ? (
          <OrderPreview preview={preview} submitting={submitting}
            onBack={() => setPreview(null)} onConfirm={submit} />
        ) : (
        <>
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-semibold numeric">{fmtCcy(quote.price, quote.currency)}</span>
          <span className={`numeric text-sm font-medium ${quote.change_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {quote.change_pct >= 0 ? "+" : ""}{quote.change_pct}%
          </span>
        </div>

        <Tabs value={side} onValueChange={(v) => { setSide(v); if (v === "buy" && isStop) setOrderType("market"); }}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="buy" data-testid="order-ticket-buy-tab">Buy</TabsTrigger>
            <TabsTrigger value="sell" data-testid="order-ticket-sell-tab">Sell</TabsTrigger>
          </TabsList>
        </Tabs>

        <Tabs value={orderType} onValueChange={setOrderType}>
          <TabsList className={`grid w-full ${side === "sell" ? "grid-cols-3" : "grid-cols-2"}`}>
            <TabsTrigger value="market" data-testid="order-ticket-market-tab">Market</TabsTrigger>
            <TabsTrigger value="limit" data-testid="order-ticket-limit-tab">Limit</TabsTrigger>
            {side === "sell" && (
              <TabsTrigger value="stop" data-testid="order-ticket-stop-tab">Stop loss</TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {orderType === "limit" && (
          <div className="space-y-1.5">
            <Label>Limit price ({quote.currency})</Label>
            <Input type="number" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)}
              placeholder={String(quote.price)} data-testid="order-ticket-price-input" />
          </div>
        )}

        {isStop && (
          <StopPanel symbol={symbol} quote={quote} qty={qty} stopPrice={stopPrice}
            setStopPrice={setStopPrice} trailPct={trailPct} setTrailPct={setTrailPct} />
        )}

        {contract && <ContractNote contract={contract} price={quote.price} unit={quote.unit} qty={Number(qty) || 0} />}
        {side === "buy" && quote.storage && (
          <StorageNote storage={{ ...quote.storage, waiver_usd: 100 }} notionalUsd={estUsd} />
        )}

        <QuantityField symbol={symbol} contract={contract} qty={qty} setQty={setQty}
          minQty={minQty} belowMin={belowMin} />

        <CostSummary currency={quote.currency} estLocal={estLocal} estUsd={estUsd} fee={fee} />

        <DialogFooter>
          <Button
            className={`w-full ${side === "buy" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
            variant={side === "buy" ? "default" : "destructive"}
            onClick={review}
            disabled={reviewing || qty <= 0 || belowMin || (isStop && !trailing && !stopPrice)}
            data-testid="order-ticket-submit">
            {reviewing ? "Preparing..."
              : trailing ? "Review trailing stop"
              : isStop ? "Review stop loss" : `Review ${side} order`}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
